import { useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const ENDPOINTS_TO_TEST = [
  { label: "Health ping",  path: "/health",                  method: "GET", auth: false },
  { label: "Admin stats",  path: "/api/admin/stats",         method: "GET", auth: true  },
  { label: "Admin users",  path: "/api/admin/users?limit=1", method: "GET", auth: true  },
  { label: "PPS metrics",  path: "/api/admin/pps/current",   method: "GET", auth: true  },
];

const RATING = (ms) => {
  if (ms === null) return { label: "—",        color: "#6b7280" };
  if (ms < 150)   return { label: "Excellent", color: "#16a34a" };
  if (ms < 400)   return { label: "Good",      color: "#65a30d" };
  if (ms < 800)   return { label: "Slow",      color: "#d97706" };
  return           { label: "Critical",  color: "#dc2626" };
};

const fmt = (ms) =>
  ms === null ? "—" : ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;

function Badge({ ms }) {
  const r = RATING(ms);
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "2px 8px",
      borderRadius: 99, background: r.color + "22", color: r.color,
      letterSpacing: "0.03em",
    }}>
      {r.label}
    </span>
  );
}

function MetricCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: "#f9fafb", border: "1px solid #e5e7eb",
      borderRadius: 10, padding: "14px 18px", minWidth: 0,
    }}>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || "#111827" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StatusDot({ ok }) {
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: ok ? "#16a34a" : "#dc2626", marginRight: 6, flexShrink: 0,
    }} />
  );
}

export function PerformanceDiagnosticTab() {
  const [running, setRunning]   = useState(false);
  const [results, setResults]   = useState(null);
  const [log, setLog]           = useState([]);
  const [progress, setProgress] = useState(0);

  const token =
    localStorage.getItem("adminToken") ||
    localStorage.getItem("token") || "";

  const appendLog = useCallback((msg) => {
    setLog((prev) => [...prev, { time: new Date().toLocaleTimeString(), msg }]);
  }, []);

  async function measureURL(url) {
    const t0 = performance.now();
    try {
      const res = await fetch(url, { cache: "no-store", mode: "cors" });
      const ttfb = performance.now() - t0;
      await res.text();
      const total = performance.now() - t0;
      return { ttfb, total, status: res.status, ok: res.ok };
    } catch {
      return { ttfb: null, total: performance.now() - t0, status: 0, ok: false };
    }
  }

  function getNavTiming() {
    const nav = performance.getEntriesByType("navigation")[0];
    if (!nav) return null;
    return {
      dns:        Math.round(nav.domainLookupEnd - nav.domainLookupStart),
      tcp:        Math.round(nav.connectEnd - nav.connectStart),
      ttfb:       Math.round(nav.responseStart - nav.requestStart),
      domLoad:    Math.round(nav.domContentLoadedEventEnd - nav.startTime),
      windowLoad: Math.round(nav.loadEventEnd - nav.startTime),
      transferKB: nav.transferSize ? (nav.transferSize / 1024).toFixed(1) : "—",
    };
  }

  async function testEndpoints() {
    const out = [];
    for (const ep of ENDPOINTS_TO_TEST) {
      const url = API_BASE + ep.path;
      const headers = ep.auth && token ? { Authorization: `Bearer ${token}` } : {};
      const t0 = performance.now();
      try {
        const res = await fetch(url, { headers, cache: "no-store" });
        const ms  = performance.now() - t0;
        out.push({ ...ep, ms: Math.round(ms), status: res.status, ok: res.ok });
        appendLog(`${ep.label}: ${Math.round(ms)}ms [${res.status}]`);
      } catch (e) {
        out.push({ ...ep, ms: null, status: 0, ok: false });
        appendLog(`${ep.label}: FAILED (${e.message})`);
      }
      await new Promise((r) => setTimeout(r, 80));
    }
    return out;
  }

  function getConnectionInfo() {
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    if (!conn) return null;
    return {
      type:     conn.effectiveType || conn.type || "unknown",
      downlink: conn.downlink ? `${conn.downlink} Mbps` : "—",
      rtt:      conn.rtt      ? `${conn.rtt}ms`         : "—",
      saveData: conn.saveData || false,
    };
  }

  async function runDiagnostic() {
    setRunning(true);
    setLog([]);
    setResults(null);
    setProgress(10);

    appendLog("Initializing diagnostic...");

    const navTiming = getNavTiming();
    appendLog("Page load timing collected.");
    setProgress(25);

    const connInfo = getConnectionInfo();
    appendLog(`Network type: ${connInfo?.type || "not detected"}`);
    setProgress(35);

    appendLog("Measuring portal TTFB...");
    const portalResult = await measureURL(window.location.origin);
    appendLog(`Portal TTFB: ${fmt(portalResult.ttfb)}, Total: ${fmt(portalResult.total)}`);
    setProgress(50);

    appendLog("Testing API endpoints...");
    const endpointResults = await testEndpoints();
    setProgress(80);

    const resources = performance
      .getEntriesByType("resource")
      .filter((r) => r.initiatorType !== "beacon")
      .map((r) => ({
        name:     r.name.split("/").pop().split("?")[0] || r.name,
        duration: Math.round(r.duration),
        type:     r.initiatorType,
        sizeKB:   r.transferSize ? (r.transferSize / 1024).toFixed(1) : null,
      }))
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 8);

    const allMs      = endpointResults.filter((e) => e.ms !== null).map((e) => e.ms);
    const avgApi     = allMs.length
      ? Math.round(allMs.reduce((a, b) => a + b, 0) / allMs.length)
      : null;
    const failedCount = endpointResults.filter((e) => !e.ok).length;

    let verdict = "ok";
    if (failedCount > 0) verdict = "error";
    else if (avgApi > 800 || (navTiming && navTiming.ttfb > 400)) verdict = "warn";

    setResults({
      navTiming, connInfo, portalResult,
      endpointResults, resources, avgApi, failedCount, verdict,
    });
    setProgress(100);
    appendLog("Diagnostic complete!");
    setRunning(false);
  }

  const verdictMap = {
    ok:   { bg: "#f0fdf4", border: "#bbf7d0", icon: "✅", text: "Everything looks healthy — server, network, and APIs are all within normal range." },
    warn: { bg: "#fffbeb", border: "#fde68a", icon: "⚠️", text: "Performance is slightly degraded — API response time or TTFB is above threshold. See details below." },
    error:{ bg: "#fef2f2", border: "#fecaca", icon: "🔴", text: "Some endpoints are failing — possible backend or authentication issue." },
  };
  const verdictStyle = results ? verdictMap[results.verdict] : null;

  return (
    <div style={{ padding: "24px 0", fontFamily: "inherit", maxWidth: 820 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 6px" }}>
          Performance Diagnostic
        </h2>
        <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
          Measures TTFB, API response time, page load, and network info directly from the browser.
          No server changes required.
        </p>
      </div>

      <button
        onClick={runDiagnostic}
        disabled={running}
        style={{
          background: running ? "#9ca3af" : "#EA580C",
          color: "#fff", border: "none", borderRadius: 8,
          padding: "10px 24px", fontSize: 14, fontWeight: 600,
          cursor: running ? "not-allowed" : "pointer",
          marginBottom: 24, transition: "background 0.2s",
        }}
      >
        {running ? "Running..." : "▶  Run Diagnostic"}
      </button>

      {running && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ background: "#e5e7eb", borderRadius: 99, height: 6, overflow: "hidden" }}>
            <div style={{
              width: `${progress}%`, height: "100%",
              background: "#EA580C", transition: "width 0.4s ease",
            }} />
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{progress}%</div>
        </div>
      )}

      {log.length > 0 && (
        <div style={{
          background: "#111827", borderRadius: 10, padding: "12px 16px",
          marginBottom: 24, fontFamily: "monospace", fontSize: 12,
          maxHeight: 140, overflowY: "auto",
        }}>
          {log.map((l, i) => (
            <div key={i} style={{ color: "#d1fae5", lineHeight: 1.7 }}>
              <span style={{ color: "#6b7280", marginRight: 8 }}>{l.time}</span>
              {l.msg}
            </div>
          ))}
        </div>
      )}

      {results && (
        <>
          {verdictStyle && (
            <div style={{
              background: verdictStyle.bg, border: `1px solid ${verdictStyle.border}`,
              borderRadius: 10, padding: "12px 16px", marginBottom: 24,
              display: "flex", alignItems: "flex-start", gap: 10,
            }}>
              <span style={{ fontSize: 18 }}>{verdictStyle.icon}</span>
              <p style={{ fontSize: 14, color: "#374151", margin: 0 }}>{verdictStyle.text}</p>
            </div>
          )}

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12, marginBottom: 28,
          }}>
            <MetricCard
              label="Portal TTFB"
              value={fmt(results.portalResult.ttfb)}
              sub="Under 200ms = good"
              color={RATING(results.portalResult.ttfb).color}
            />
            <MetricCard
              label="Page Total Load"
              value={results.navTiming ? fmt(results.navTiming.windowLoad) : "—"}
              sub="window.onload time"
              color={RATING(results.navTiming?.windowLoad).color}
            />
            <MetricCard
              label="Avg API Time"
              value={fmt(results.avgApi)}
              sub={`${results.endpointResults.length} endpoints tested`}
              color={RATING(results.avgApi).color}
            />
            <MetricCard
              label="Failed APIs"
              value={results.failedCount}
              sub={results.failedCount === 0 ? "All passing" : "Endpoints failed"}
              color={results.failedCount === 0 ? "#16a34a" : "#dc2626"}
            />
          </div>

          {results.navTiming && (
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
                Page Load Breakdown
              </h3>
              <div style={{
                background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 10, overflow: "hidden",
              }}>
                {[
                  { label: "DNS Lookup",  ms: results.navTiming.dns,        tip: "50ms+ = DNS slow" },
                  { label: "TCP Connect", ms: results.navTiming.tcp,        tip: "100ms+ = network latency" },
                  { label: "Server TTFB", ms: results.navTiming.ttfb,       tip: "400ms+ = server slow" },
                  { label: "DOM Ready",   ms: results.navTiming.domLoad,    tip: "HTML parse + render" },
                  { label: "Full Load",   ms: results.navTiming.windowLoad, tip: "Total page load" },
                ].map((row, i, arr) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between", padding: "10px 16px",
                    borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none",
                  }}>
                    <div>
                      <span style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>
                        {row.label}
                      </span>
                      <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: 8 }}>
                        {row.tip}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: RATING(row.ms).color }}>
                        {fmt(row.ms)}
                      </span>
                      <Badge ms={row.ms} />
                    </div>
                  </div>
                ))}
                <div style={{
                  padding: "8px 16px", background: "#f3f4f6",
                  fontSize: 12, color: "#6b7280",
                }}>
                  Transfer size: {results.navTiming.transferKB} KB
                </div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 28 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
              API Endpoints Test
            </h3>
            <div style={{
              background: "#f9fafb", border: "1px solid #e5e7eb",
              borderRadius: 10, overflow: "hidden",
            }}>
              {results.endpointResults.map((ep, i, arr) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between", padding: "10px 16px",
                  borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none",
                }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <StatusDot ok={ep.ok} />
                    <div>
                      <span style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>
                        {ep.label}
                      </span>
                      <code style={{
                        fontSize: 11, color: "#9ca3af", marginLeft: 8,
                        background: "#e5e7eb", padding: "1px 6px", borderRadius: 4,
                      }}>
                        {ep.path}
                      </code>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 12, color: ep.ok ? "#16a34a" : "#dc2626" }}>
                      {ep.status || "ERR"}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: RATING(ep.ms).color }}>
                      {fmt(ep.ms)}
                    </span>
                    <Badge ms={ep.ms} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {results.resources.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
                Slowest Assets (Top 8)
              </h3>
              <div style={{
                background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 10, overflow: "hidden",
              }}>
                {results.resources.map((r, i, arr) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between", padding: "8px 16px",
                    borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none",
                  }}>
                    <div style={{ minWidth: 0, overflow: "hidden" }}>
                      <span style={{
                        fontSize: 12, fontFamily: "monospace", color: "#374151",
                        display: "block", overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 380,
                      }}>
                        {r.name}
                      </span>
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>
                        {r.type}{r.sizeKB ? ` · ${r.sizeKB} KB` : ""}
                      </span>
                    </div>
                    <span style={{
                      fontSize: 14, fontWeight: 600,
                      color: RATING(r.duration).color, flexShrink: 0,
                    }}>
                      {fmt(r.duration)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {results.connInfo && (
            <div style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
                Network / Connection Info
              </h3>
              <div style={{
                background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 10, padding: "12px 16px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 12,
              }}>
                {[
                  { label: "Type",       value: results.connInfo.type.toUpperCase() },
                  { label: "Downlink",   value: results.connInfo.downlink },
                  { label: "RTT",        value: results.connInfo.rtt },
                  { label: "Data Saver", value: results.connInfo.saveData ? "On" : "Off" },
                ].map((item, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{item.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8, marginBottom: 0 }}>
                Note: Connection info is only available in Chrome and Edge.
              </p>
            </div>
          )}

          <div style={{
            background: "#eff6ff", border: "1px solid #bfdbfe",
            borderRadius: 10, padding: "14px 18px",
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "#1d4ed8", margin: "0 0 10px" }}>
              How to Read Results
            </h3>
            <div style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.9 }}>
              <div>🟢 <strong>TTFB &lt; 200ms</strong> — Server and Nginx are healthy. Issue is on the client or network side.</div>
              <div>🟡 <strong>TTFB 200–400ms</strong> — Acceptable. Consider optimising FastAPI or database queries.</div>
              <div>🔴 <strong>TTFB &gt; 400ms</strong> — Server is slow. Check Gunicorn workers, database queries, RAM and CPU usage.</div>
              <div>🔴 <strong>DNS &gt; 50ms</strong> — DNS resolver is slow. Consider using Cloudflare 1.1.1.1.</div>
              <div>🔴 <strong>API failing</strong> — Backend may be down or there is an auth issue. Check server logs.</div>
            </div>
          </div>

          <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 16, marginBottom: 0 }}>
            Test run:{" "}
            {new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST
          </p>
        </>
      )}
    </div>
  );
}
